import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import SettingsIcon from '@material-ui/icons/Settings';
import axios from 'axios';
import { storeContext } from '../provider/Provider';
import AiInsights from './AiInsights';
import CompetitorAnalysis from './CompetitorAnalysis';
import GeoVisibilityConfig from './GeoVisibilityConfig';
import { geoVisibilityActions } from '../../store/geo-visibility-reducer';

const { REACT_APP_FLASK_API_URL } = process.env;

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: 20,
    [theme.breakpoints.down('sm')]: {
      margin: '0 15px',
    },
  },
  heading: {
    fontWeight: 700,
    fontSize: 25,
    color: '#58595B',
    marginBottom: 16,
  },
  card: {
    padding: 20,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    marginBottom: 20,
  },
  gridCell: {
    textAlign: 'center',
    fontWeight: 600,
    fontSize: 13,
    padding: '8px 12px',
    cursor: 'pointer',
    borderRadius: 6,
    transition: 'transform 0.1s',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  practiceAreaChip: {
    margin: '0 4px 4px 0',
    fontWeight: 600,
  },
  statBox: {
    padding: 16,
    background: 'white',
    borderRadius: 16,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    textAlign: 'center',
    flex: 1,
    minWidth: 140,
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    fontFamily: 'monospace',
  },
  statLabel: {
    fontSize: 11,
    color: '#999',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  sovBar: {
    height: 20,
    borderRadius: 4,
    transition: 'width 0.3s ease',
  },
  pin: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    border: '2px solid rgba(255,255,255,0.8)',
    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
    fontFamily: 'monospace',
  },
  progressBar: {
    marginBottom: 16,
    borderRadius: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#58595B',
    marginBottom: 12,
  },
  rankBadge: {
    display: 'inline-block',
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: 12,
    padding: '2px 8px',
    borderRadius: 4,
    minWidth: 32,
    textAlign: 'center',
  },
}));

function getRankColor(rank) {
  if (rank <= 3) return '#10b981';
  if (rank <= 6) return '#f59e0b';
  return '#ef4444';
}

function getRankBg(rank) {
  if (rank <= 3) return '#10b98120';
  if (rank <= 6) return '#f59e0b20';
  return '#ef444420';
}

function getColor(pct) {
  if (pct === null || pct === undefined) return '#e5e7eb';
  if (pct >= 70) return '#10b981';
  if (pct >= 40) return '#f59e0b';
  if (pct > 0) return '#ef4444';
  return '#e5e7eb';
}

function getLabel(pct) {
  if (pct === null || pct === undefined) return '\u2014';
  return `${pct}%`;
}

const GeoVisibilityOverview = () => {
  const classes = useStyles();
  const { state, dispatch } = useContext(storeContext);
  const domain = state.websiteSwitch.currentWebsite;
  const { gridResults, gridLoading, gridError, gridProgress } = state.geoVisibility;

  const [selectedPA, setSelectedPA] = useState(null);
  const [cellDialog, setCellDialog] = useState(null);
  const [cellRankings, setCellRankings] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const pollRef = useRef(null);

  // Fetch grid results
  const fetchGrid = useCallback(() => {
    if (!domain) return;
    dispatch({ type: geoVisibilityActions.GEO_SET_GRID_LOADING });
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/grid-results`)
      .then((res) => {
        if (!res.data.error) {
          dispatch({ type: geoVisibilityActions.GEO_SET_GRID, payload: res.data.data });
          if (!selectedPA && res.data.data.practice_areas?.length > 0) {
            setSelectedPA(res.data.data.practice_areas[0].name);
          }
        } else {
          dispatch({ type: geoVisibilityActions.GEO_SET_GRID_ERROR, payload: res.data.message });
        }
      })
      .catch((err) => {
        dispatch({ type: geoVisibilityActions.GEO_SET_GRID_ERROR, payload: err.message });
      });
  }, [domain, dispatch, selectedPA]);

  // Fetch AI insights
  const fetchInsights = useCallback(() => {
    if (!domain) return;
    setInsightsLoading(true);
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/ai-insights`)
      .then((res) => {
        if (!res.data.error) setInsights(res.data.data);
      })
      .catch(() => {})
      .finally(() => setInsightsLoading(false));
  }, [domain]);

  // Fetch aggregated metrics for SOV
  const fetchMetrics = useCallback(() => {
    if (!domain) return;
    axios
      .post(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/llm-mentions/aggregated-metrics`, {
        domain,
        platform: 'chat_gpt',
      })
      .then((res) => {
        if (!res.data.error) {
          setMetrics(res.data.data);
        }
      })
      .catch(() => {});
  }, [domain]);

  useEffect(() => {
    fetchGrid();
    fetchMetrics();
    fetchInsights();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [domain]);

  // Run grid search
  const handleRunSearch = () => {
    axios
      .post(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/grid-run`)
      .then((res) => {
        if (!res.data.error && res.data.data.status === 'started') {
          dispatch({
            type: geoVisibilityActions.GEO_SET_PROGRESS,
            payload: { completed: 0, total: res.data.data.stale_count, running: true },
          });
          // Start polling
          pollRef.current = setInterval(() => {
            axios
              .get(`${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/grid-progress`)
              .then((pRes) => {
                if (!pRes.data.error) {
                  const prog = pRes.data.data;
                  dispatch({ type: geoVisibilityActions.GEO_SET_PROGRESS, payload: prog });
                  if (!prog.running) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    fetchGrid();
                    fetchInsights();
                  }
                }
              })
              .catch(() => {});
          }, 3000);
        }
      })
      .catch(() => {});
  };

  // Open cell rankings dialog
  const handleCellClick = (practiceArea, city) => {
    setCellDialog({ practiceArea, city });
    setCellRankings(null);
    axios
      .get(
        `${REACT_APP_FLASK_API_URL}/v1/geo-visibility/${domain}/cell-rankings?practice_area=${encodeURIComponent(practiceArea)}&city=${encodeURIComponent(city)}`
      )
      .then((res) => {
        if (!res.data.error) {
          setCellRankings(res.data.data);
        }
      })
      .catch(() => {});
  };

  // Derived data
  const practiceAreas = gridResults?.practice_areas || [];
  const locations = gridResults?.locations || [];
  const results = gridResults?.results || {};

  const selectedRow = selectedPA ? results[selectedPA] : null;

  // Compute stats for selected practice area
  const stats = useMemo(() => {
    if (!selectedRow) return { visible: 0, avg: 0, strong: 0, total: 0 };
    const nonNull = selectedRow.filter((v) => v !== null && v !== undefined);
    const visible = nonNull.filter((v) => v > 0).length;
    const avg = nonNull.length > 0 ? Math.round(nonNull.reduce((a, b) => a + b, 0) / nonNull.length) : 0;
    const strong = nonNull.filter((v) => v >= 70).length;
    return { visible, avg, strong, total: locations.length };
  }, [selectedRow, locations]);

  // SOV data
  const sovData = useMemo(() => {
    if (!metrics?.total?.sources_domain) return [];
    const items = metrics.total.sources_domain.slice(0, 8);
    const max = items.length > 0 ? Math.max(...items.map((d) => d.mentions || 0)) : 1;
    return items.map((d) => ({
      domain: d.key,
      mentions: d.mentions || 0,
      pct: max > 0 ? Math.round(((d.mentions || 0) / max) * 100) : 0,
      isTarget: d.key?.includes(domain?.split('.')[0]),
    }));
  }, [metrics, domain]);

  if (!domain) {
    return (
      <div className={classes.wrapper}>
        <Typography className={classes.heading}>AI Visibility Overview</Typography>
        <Paper className={classes.card}>
          <Typography>Please select a website first.</Typography>
        </Paper>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography className={classes.heading}>AI Visibility Overview</Typography>
        <Box display="flex" style={{ gap: 8 }}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setConfigOpen(true)}
            style={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 8,
              borderColor: '#ccc',
            }}
          >
            Settings
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRunSearch}
            disabled={gridProgress.running}
            style={{
              background: gridProgress.running ? '#ccc' : '#0d9373',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 8,
            }}
          >
            {gridProgress.running ? 'Searching...' : 'Run Search'}
          </Button>
        </Box>
      </Box>

      {/* Progress bar */}
      {gridProgress.running && (
        <Paper className={classes.card} style={{ padding: 12 }}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              Querying ChatGPT...
            </Typography>
            <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {gridProgress.completed}/{gridProgress.total}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={gridProgress.total > 0 ? (gridProgress.completed / gridProgress.total) * 100 : 0}
            className={classes.progressBar}
          />
        </Paper>
      )}

      {gridLoading && !gridResults && <LinearProgress style={{ marginBottom: 16 }} />}
      {gridError && (
        <Paper className={classes.card}>
          <Typography color="error">{gridError}</Typography>
          <Button onClick={fetchGrid} style={{ marginTop: 8 }}>Retry</Button>
        </Paper>
      )}

      {gridResults && (
        <>
          {/* Practice area chips + stats */}
          <Paper className={classes.card}>
            <Typography className={classes.sectionTitle}>Practice Areas</Typography>
            <Box mb={2}>
              {practiceAreas.map((pa) => (
                <Chip
                  key={pa.name}
                  label={pa.name}
                  onClick={() => setSelectedPA(pa.name)}
                  className={classes.practiceAreaChip}
                  style={{
                    background: selectedPA === pa.name ? '#e8f5e9' : '#f5f5f5',
                    border: selectedPA === pa.name ? '2px solid #0d9373' : '2px solid transparent',
                    color: selectedPA === pa.name ? '#0d9373' : '#666',
                  }}
                />
              ))}
            </Box>

            {/* Stat cards scoped to selected practice area */}
            {selectedPA && (
              <Box display="flex" flexWrap="wrap" style={{ gap: 12 }} mb={2}>
                <div className={classes.statBox}>
                  <div className={classes.statLabel}>Visible Locations ({selectedPA})</div>
                  <div className={classes.statValue} style={{ color: '#0d9373' }}>
                    {stats.visible}/{stats.total}
                  </div>
                </div>
                <div className={classes.statBox}>
                  <div className={classes.statLabel}>Avg Visibility ({selectedPA})</div>
                  <div className={classes.statValue} style={{ color: '#0d9373' }}>{stats.avg}%</div>
                </div>
                <div className={classes.statBox}>
                  <div className={classes.statLabel}>Strong Locations ({selectedPA})</div>
                  <div className={classes.statValue} style={{ color: '#10b981' }}>{stats.strong}</div>
                </div>
                <div className={classes.statBox}>
                  <div className={classes.statLabel}>Stale Cells</div>
                  <div className={classes.statValue} style={{ color: gridResults.stale_count > 0 ? '#f59e0b' : '#10b981' }}>
                    {gridResults.stale_count}
                  </div>
                </div>
              </Box>
            )}

            {/* Location pins (map placeholder) */}
            {locations.length > 0 && (
              <Box
                style={{
                  background: '#f0f4f8',
                  borderRadius: 12,
                  padding: 20,
                  position: 'relative',
                }}
              >
                <Typography variant="caption" color="textSecondary" style={{ marginBottom: 12, display: 'block' }}>
                  Visibility by Location
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
                  {locations.map((loc, i) => {
                    const pct = selectedRow ? selectedRow[i] : null;
                    return (
                      <Box
                        key={loc.name}
                        onClick={() => { if (selectedPA) handleCellClick(selectedPA, loc.name); }}
                        style={{
                          cursor: 'pointer',
                          textAlign: 'center',
                          padding: '8px 12px',
                          borderRadius: 12,
                          background: 'white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          minWidth: 100,
                        }}
                      >
                        <div
                          className={classes.pin}
                          style={{ background: getColor(pct), margin: '0 auto 6px' }}
                        >
                          {getLabel(pct)}
                        </div>
                        <Typography variant="caption" style={{ fontWeight: 600, fontSize: 11 }}>
                          {loc.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                {/* Legend */}
                <Box display="flex" style={{ gap: 12, marginTop: 12 }}>
                  {[
                    { color: '#10b981', label: '70%+' },
                    { color: '#f59e0b', label: '40-69%' },
                    { color: '#ef4444', label: '<40%' },
                    { color: '#e5e7eb', label: 'None' },
                  ].map(({ color, label }) => (
                    <Box key={label} display="flex" alignItems="center" style={{ gap: 4 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
                      <span style={{ fontSize: 10, color: '#666' }}>{label}</span>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          {/* Grid table */}
          <Paper className={classes.card}>
            <Typography className={classes.sectionTitle}>Visibility Grid</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 700, fontSize: 11, color: '#999' }}>Practice Area</TableCell>
                    {locations.map((loc) => (
                      <TableCell key={loc.name} align="center" style={{ fontWeight: 700, fontSize: 11, color: '#999' }}>
                        {loc.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {practiceAreas.map((pa) => (
                    <TableRow key={pa.name} hover>
                      <TableCell style={{ fontWeight: 600, fontSize: 13 }}>{pa.name}</TableCell>
                      {locations.map((loc, i) => {
                        const pct = results[pa.name] ? results[pa.name][i] : null;
                        return (
                          <TableCell
                            key={loc.name}
                            align="center"
                            onClick={() => handleCellClick(pa.name, loc.name)}
                            style={{ cursor: 'pointer' }}
                          >
                            <span
                              className={classes.gridCell}
                              style={{
                                background: `${getColor(pct)}20`,
                                color: getColor(pct) === '#e5e7eb' ? '#999' : getColor(pct),
                                display: 'inline-block',
                                padding: '4px 10px',
                                borderRadius: 6,
                              }}
                            >
                              {getLabel(pct)}
                            </span>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

        </>
      )}

      {/* AI Insights */}
      <AiInsights insights={insights} loading={insightsLoading} />

      {/* Competitor Deep Dive */}
      <CompetitorAnalysis />

      {/* Share of Voice */}
      {gridResults && sovData.length > 0 && (
        <Paper className={classes.card}>
          <Typography className={classes.sectionTitle}>Share of Voice</Typography>
          {sovData.map((item) => (
            <Box key={item.domain} display="flex" alignItems="center" mb={1} style={{ gap: 8 }}>
              <Typography
                variant="body2"
                style={{
                  width: 160,
                  fontSize: 12,
                  fontWeight: item.isTarget ? 700 : 400,
                  color: item.isTarget ? '#0d9373' : '#666',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.domain}
              </Typography>
              <Box flex={1}>
                <div
                  className={classes.sovBar}
                  style={{
                    width: `${item.pct}%`,
                    background: item.isTarget ? '#0d9373' : '#cbd5e1',
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 12, minWidth: 40, textAlign: 'right' }}
              >
                {item.mentions}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Config Dialog */}
      <GeoVisibilityConfig
        open={configOpen}
        onClose={() => {
          setConfigOpen(false);
          fetchGrid();
          fetchInsights();
        }}
      />

      {/* Cell Rankings Dialog */}
      <Dialog open={!!cellDialog} onClose={() => setCellDialog(null)} maxWidth="sm" fullWidth>
        {cellDialog && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <span>
                  {cellDialog.practiceArea} in {cellDialog.city}
                </span>
                <IconButton size="small" onClick={() => setCellDialog(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              {/* eslint-disable-next-line no-nested-ternary */}
              {!cellRankings ? (
                <LinearProgress />
              ) : !cellRankings.groups || cellRankings.groups.length === 0 ? (
                <Typography color="textSecondary">No rankings found for this cell.</Typography>
              ) : (
                cellRankings.groups.map((group, gi) => (
                  /* eslint-disable-next-line react/no-array-index-key */
                  <Box key={gi} mb={gi < cellRankings.groups.length - 1 ? 3 : 0}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                      style={{
                        background: '#f8f9fa',
                        borderRadius: 8,
                        padding: '8px 12px',
                      }}
                    >
                      <Typography variant="body2" style={{ fontWeight: 700, fontSize: 12, color: '#333' }}>
                        {group.template_label || `Template ${gi + 1}`}
                      </Typography>
                      {group.searched_at && (
                        <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>
                          {new Date(group.searched_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ display: 'block', marginBottom: 8, fontSize: 11, fontStyle: 'italic' }}
                    >
                      {group.template_text}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ fontWeight: 700, fontSize: 11 }}>#</TableCell>
                          <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Firm</TableCell>
                          <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Domain</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.rankings.map((r) => {
                          const isTarget = r.domain?.includes(domain?.split('.')[0]);
                          return (
                            <TableRow key={r.rank} style={{ background: isTarget ? '#f0fdf4' : undefined }}>
                              <TableCell>
                                <span
                                  className={classes.rankBadge}
                                  style={{
                                    background: getRankBg(r.rank),
                                    color: getRankColor(r.rank),
                                  }}
                                >
                                  #{r.rank}
                                </span>
                              </TableCell>
                              <TableCell style={{ fontWeight: isTarget ? 700 : 400, color: isTarget ? '#0d9373' : undefined }}>
                                {r.firm_name}
                              </TableCell>
                              <TableCell style={{ fontSize: 12, color: '#666' }}>{r.domain || '\u2014'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    {/* Fan-out queries — what ChatGPT actually searched */}
                    {group.fan_out_queries && group.fan_out_queries.length > 0 && (
                      <Box mt={1} mb={1}>
                        <Typography variant="caption" style={{ fontWeight: 700, color: '#999', display: 'block', marginBottom: 4 }}>
                          ChatGPT&apos;s Internal Search
                        </Typography>
                        {group.fan_out_queries.map((q) => (
                          <Typography key={q} variant="caption" style={{ display: 'block', fontSize: 11, color: '#666', fontStyle: 'italic' }}>
                            &ldquo;{q}&rdquo;
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {/* Citations — sources ChatGPT actually referenced */}
                    {group.annotations && group.annotations.length > 0 && (
                      <Box
                        mt={1}
                        style={{ background: '#f0f7ff', borderRadius: 8, padding: '8px 12px' }}
                      >
                        <Typography variant="caption" style={{ fontWeight: 700, color: '#3b82f6', display: 'block', marginBottom: 4 }}>
                          Sources Cited ({group.annotations.length})
                        </Typography>
                        {group.annotations.slice(0, 10).map((ann) => {
                          const urlDomain = ann.url ? new URL(ann.url).hostname.replace('www.', '') : '';
                          const isClientCitation = urlDomain && domain && urlDomain.includes(domain.split('.')[0]);
                          return (
                            <Box
                              key={ann.url}
                              display="flex"
                              alignItems="center"
                              style={{ gap: 6, padding: '2px 0' }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  background: isClientCitation ? '#10b981' : '#94a3b8',
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="caption"
                                style={{
                                  fontSize: 11,
                                  color: isClientCitation ? '#0d9373' : '#666',
                                  fontWeight: isClientCitation ? 700 : 400,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {ann.title || urlDomain}
                              </Typography>
                              <Typography variant="caption" style={{ fontSize: 10, color: '#999', flexShrink: 0 }}>
                                {urlDomain}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default GeoVisibilityOverview;
